import { api } from "@/lib/api/client";
import type { Page } from "@/lib/api/types";
import type { ChangeRequest, Completeness, EmployeeProfile } from "@/features/profile/types";
import type { ReviewFlags, ReviewMark } from "@/features/profile/reviewFlags";

export interface EmployeeListItem {
  user_id: string;
  employee_id: string | null;
  full_name_en: string;
  full_name_ar: string | null;
  email: string;
  account_status: "draft" | "pending" | "active" | "rejected" | "disabled";
  employment_status: "active" | "probation" | "leave" | "resigned" | "terminated";
  position_title: string | null;
  home_branch_id: string | null;
  home_branch_name: string | null;
  date_of_joining: string | null;
  /** The last working day, for somebody who has left through the system. */
  left_on: string | null;
  profile_picture_file_id: string | null;
  /** When probation ends, so the list and the record say the same about it. */
  probation_end_date?: string | null;
  /** Corrections asked of an active account: with them, or sent back to HR. */
  corrections?: "requested" | "returned" | null;
}

export interface EmployeeFilters {
  account_status?: string;
  employment_status?: string;
  branch_id?: string;
  q?: string;
  /** Joined on or after, YYYY-MM-DD. */
  joined_from?: string;
  /** Joined on or before, YYYY-MM-DD. */
  joined_to?: string;
  sort?: string;
  page?: number;
  page_size?: number;
}

export interface ApprovePayload {
  employee_id?: string | null;
  home_branch_id?: string | null;
  badge_number?: string | null;
  employment_status?: string;
  /** Every position held, the primary first. Two for a dual role. */
  position_ids?: string[];
  /** YYYY-MM-DD. Six months after joining when left out. */
  probation_end_date?: string | null;
  /** A UAE national's monthly GPSSA contribution. Required for them. */
  gpssa_amount?: string | null;
  note?: string | null;
}

/** The approved record, and what approving it sent the new joiner to sign. */
export interface ApprovalResult extends EmployeeProfile {
  joining_forms_sent: number;
  job_descriptions_sent: number;
}

export interface FieldChange {
  field: string;
  label: string;
  old: string | null;
  new: string | null;
  is_money: boolean;
}

export interface HistoryEntry {
  id: number;
  at: string;
  action: string;
  action_label: string;
  actor_name: string | null;
  actor_is_self: boolean;
  changes: FieldChange[];
  note: string | null;
}

export interface TemporaryPassword {
  password: string;
  expires_at: string;
  message: string;
}

export interface EmailChanged {
  email: string;
  message: string;
  /** What became of the reset link, when one was asked for. */
  reset_outcome: "sent" | "mail_off" | "failed" | "suspended" | null;
}

export const employeeKeys = {
  all: ["employees"] as const,
  list: (filters: EmployeeFilters) => [...employeeKeys.all, "list", filters] as const,
  detail: (id: string) => [...employeeKeys.all, "detail", id] as const,
  nextId: () => [...employeeKeys.all, "next-id"] as const,
  completeness: (id: string) => [...employeeKeys.all, "completeness", id] as const,
  history: (id: string) => [...employeeKeys.all, "history", id] as const,
  changeRequests: (status?: string) =>
    [...employeeKeys.all, "change-requests", status ?? "pending"] as const,
};

export const employeeApi = {
  list: (filters: EmployeeFilters) =>
    api.get<Page<EmployeeListItem>>("/employees", { query: { ...filters } }),

  detail: (userId: string) => api.get<EmployeeProfile>(`/employees/${userId}`),

  update: (userId: string, changes: Record<string, unknown>) =>
    api.patch<EmployeeProfile>(`/employees/${userId}/profile`, changes),

  setApprover: (userId: string, isApprover: boolean) =>
    api.post<EmployeeProfile>(`/employees/${userId}/approver`, { is_approver: isApprover }),
  approve: (userId: string, payload: ApprovePayload) =>
    api.post<ApprovalResult>(`/employees/${userId}/approve`, payload),

  /** What the employee's own profile still lacks, as the wizard counts it. */
  completeness: (userId: string) =>
    api.get<Completeness>(`/employees/${userId}/completeness`),

  /** The reason may be empty when HR has crossed the fields that are wrong. */
  reject: (userId: string, reason: string) =>
    api.post<{ message: string }>(`/employees/${userId}/reject`, { reason: reason || null }),

  /**
   * Send an approved employee's profile back for corrections. Their account
   * stays active; the crosses say what to change.
   */
  requestCorrections: (userId: string, reason: string) =>
    api.post<{ message: string }>(`/employees/${userId}/request-corrections`, {
      reason: reason || null,
    }),

  /** The crosses become ticks. Nothing about approval runs again. */
  acceptCorrections: (userId: string) =>
    api.post<{ message: string }>(`/employees/${userId}/accept-corrections`),

  /** Tick, cross or clear fields. Returns every mark on the profile. */
  setReviewFlags: (userId: string, marks: Record<string, ReviewMark>) =>
    api.put<ReviewFlags>(`/employees/${userId}/review-flags`, marks),

  /** The reason goes on the audit trail and the record's history. */
  disable: (userId: string, reason?: string) =>
    api.post<{ message: string }>(`/employees/${userId}/disable`, { reason: reason || null }),
  enable: (userId: string) => api.post<{ message: string }>(`/employees/${userId}/enable`),

  nextEmployeeId: () => api.get<{ employee_id: string }>("/employees/next-id"),

  /** Every position somebody holds, the first primary. */
  setPositions: (userId: string, positionIds: string[]) =>
    api.put<EmployeeProfile>(`/employees/${userId}/positions`, { position_ids: positionIds }),

  history: (userId: string) => api.get<HistoryEntry[]>(`/employees/${userId}/history`),

  uploadFile: (userId: string, kind: string, file: File) => {
    const body = new FormData();
    body.append("file", file);
    return api.post<EmployeeProfile>(`/employees/${userId}/files/${kind}`, body);
  },

  removeFile: (userId: string, kind: string) =>
    api.delete<EmployeeProfile>(`/employees/${userId}/files/${kind}`),

  /** A one-time password for HR to hand over. Shown once; only its hash is kept. */
  temporaryPassword: (userId: string) =>
    api.post<TemporaryPassword>(`/employees/${userId}/temporary-password`),

  changeEmail: (userId: string, email: string, sendResetLink: boolean) =>
    api.patch<EmailChanged>(`/employees/${userId}/email`, {
      email,
      send_reset_link: sendResetLink,
    }),

  /** For good. `confirm` repeats the employee number, or the email without one. */
  remove: (userId: string, confirm: string) =>
    api.delete<{ message: string }>(`/employees/${userId}`, { body: { confirm } }),

  listChangeRequests: (status = "pending") =>
    api.get<ChangeRequest[]>("/change-requests", { query: { status } }),

  approveChangeRequest: (id: string, note?: string) =>
    api.post<{ message: string }>(`/change-requests/${id}/approve`, { note: note ?? null }),

  rejectChangeRequest: (id: string, note?: string) =>
    api.post<{ message: string }>(`/change-requests/${id}/reject`, { note: note ?? null }),
};
